import React, {useEffect, useMemo, useState} from 'react';
import {Col, Modal, Row, Form, InputGroup} from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import SelectInputField from '../../components/inputs/SelectInputField';
import StringInputField from '../../components/inputs/StringInputField';
import NumberInputField from '../../components/inputs/NumberInputField';
import {RRTruckType} from '../../types/RRTruckType';
import {RRFuelInvoiceType} from '../../types/RRFuelInvoiceType';
import {SaveIconButton, CancelIconButton} from '../../components/IconButtons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendar} from '@fortawesome/free-solid-svg-icons';
import {dateWithZeroTime, dateToString, showErrorsListInToast, zeroToEmptyStringAsCurrency, numberWithCommas} from '../../tools';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { appFirestore } from '../../../index';
import { fuelInvoiceConverter } from './fuelInvoiceConverter';

interface Props {
    invoice: RRFuelInvoiceType | null;
    trucks: RRTruckType[] | undefined;
    filteredEmployees: any[];
    onSave: (invoice: RRFuelInvoiceType) => void | Promise<void>;
    onClose: () => void;
}

const FuelInvoiceEditModal: React.FC<Props> = ({invoice, trucks, filteredEmployees, onSave, onClose}) => {
    const [edited, setEdited] = useState<RRFuelInvoiceType | null>(invoice);
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState<boolean>(false);
    const [, setLastFetchMessage] = useState<string | null>(null);
    const currentInvoiceId = edited?.id ?? "";
    const currentTruckId = edited?.truckId ?? "";

    useEffect(() => {
        setEdited(invoice);
        setErrors([]);
    }, [invoice]);

    const setField = (patch: Partial<RRFuelInvoiceType>) => {
        setEdited(prev => prev ? {...prev, ...patch} : prev);
    };

    // NEW: when adding a new invoice (invoice has no id) and truckId changes, fetch the largest endKm for that truck
    useEffect(() => {
        console.debug('FuelInvoiceEditModal: effect triggered', { edited });
        const fetchLatestEndKm = async (truckId: string) => {
            try {
                const msgStart = `Fetching latest endKm for truck ${truckId}...`;
                console.info(msgStart);
                setLastFetchMessage(msgStart);
                 // query fuelInvoices where truckId == truckId ordered by endKm desc limit 1
                 const q = query(
                     collection(appFirestore, 'fuelInvoices').withConverter(fuelInvoiceConverter),
                     where('truckId', '==', truckId),
                     orderBy('endKm', 'desc'),
                     limit(1)
                 );
                 const snap = await getDocs(q);
                if (snap.empty) {
                    const msgNo = `No previous endKm found for truck ${truckId}`;
                    console.info(msgNo);
                    setLastFetchMessage(msgNo);
                }
                if (!snap.empty) {
                    const doc = snap.docs[0].data();
                    const latestEndKm = Number(doc.endKm || 0);
                    const msgFound = `Latest endKm for truck ${truckId} is ${latestEndKm}`;
                    console.info(msgFound);
                    setLastFetchMessage(msgFound);
                     // Only set startKm if we're in add mode (no id) and startKm is falsy (not set) or less than latestEndKm
                     setEdited(prev => {
                         if (!prev) return prev;
                         if (prev.id) return prev; // edit mode - do nothing
                         // if existing startKm is undefined/null/0, or less than latestEndKm, set it
                         const currStart = Number(prev.startKm || 0);
                         if (!currStart || currStart < latestEndKm) {
                            const msgSet = `Setting startKm from ${currStart} to ${latestEndKm} for new invoice (truck ${truckId})`;
                            console.info(msgSet);
                            setLastFetchMessage(msgSet);
                             return {...prev, startKm: latestEndKm};
                         }
                         return prev;
                     });
                 }
             } catch (e) {
                 // log the error to the console so the developer can see why fetching failed
                const msgErr = `Failed to fetch latest endKm for truck ${truckId}: ${String((e as any)?.message || e)}`;
                console.error(msgErr, e);
                setLastFetchMessage(msgErr);
                try {
                    showErrorsListInToast('Грешка при извличане на километраж', [String((e as any)?.message || e)]);
                } catch (_err) {
                    // ignore toast errors
                }
             }
         };

        if (!edited) {
            console.debug('FuelInvoiceEditModal: edited is null - skipping fetch');
            return;
        }
        // Автопопълване на началния километраж само при нов запис и при смяна на превозното средство.
        if (!currentInvoiceId && currentTruckId) {
            console.debug('FuelInvoiceEditModal: will fetch latest endKm (add mode, truck selected)', { id: currentInvoiceId, truckId: currentTruckId });
            fetchLatestEndKm(currentTruckId);
        } else {
            console.debug('FuelInvoiceEditModal: not fetching latest endKm', { id: currentInvoiceId, truckId: currentTruckId });
        }
    }, [currentInvoiceId, currentTruckId]);

    // computed values
    const computedTotalKm = useMemo(() => {
        if (!edited) return 0;
        const s = Number(edited.startKm || 0);
        const e = Number(edited.endKm || 0);
        const t = e - s;
        return isNaN(t) ? 0 : t;
    }, [edited]);

    const computedAvgPriceForKm = useMemo(() => {
        if (!edited) return 0;
        const totalKm = computedTotalKm;
        // compute total for invoice as price * fuelAmount (цената за фактурата)
        const totalForInvoice = Number(edited.price || 0) * Number(edited.fuelAmount || 0);
        if (totalKm <= 0) return 0;
        const avg = totalForInvoice / totalKm;
        return isFinite(avg) ? avg : 0;
    }, [edited, computedTotalKm]);

    // NEW: total amount for invoice (Цена * Количество гориво)
    const computedTotalForInvoice = useMemo(() => {
        if (!edited) return 0;
        const price = Number(edited.price || 0);
        const amount = Number(edited.fuelAmount || 0);
        const t = price * amount;
        // round to 2 decimal places and return
        return isFinite(t) ? Math.round((t + Number.EPSILON) * 100) / 100 : 0;
    }, [edited]);

    // validation
    useEffect(() => {
        if (!edited) return;
        const errs: string[] = [];
        if (!edited.invNumber || (edited.invNumber || '').toString().trim() === '') errs.push('Липсва Фактура №');
        if (!edited.invDate) errs.push('Липсва Дата');
        if (!edited.truckId || (edited.truckId || '') === '') errs.push('Липсва Превозно средство');
        if (!edited.driverId || (edited.driverId || '') === '') errs.push('Липсва Шофьор');
        if (edited.price === undefined || edited.price === null || Number(edited.price) <= 0) errs.push('Невалидна Цена');
        if (edited.fuelAmount === undefined || edited.fuelAmount === null || Number(edited.fuelAmount) <= 0) errs.push('Невалидно Количество гориво');
        if (edited.startKm === undefined || edited.startKm === null) errs.push('Липсва Начален км');
        if (edited.endKm === undefined || edited.endKm === null) errs.push('Липсва Краен км');
        if (computedTotalKm <= 0) errs.push('Общо км трябва да е положително (Краен км > Начален км)');
        if (computedAvgPriceForKm <= 0) errs.push('Средна цена/км трябва да е положително число');
        setErrors(errs);
    }, [edited, computedTotalKm, computedAvgPriceForKm]);

    const doSave = async () => {
        if (!edited) return;
        if (errors.length) return;
        const payload: RRFuelInvoiceType = {
            ...edited,
            totalKm: computedTotalKm,
            avgPriceForKm: computedAvgPriceForKm,
            totalForInvoice: computedTotalForInvoice,
        } as RRFuelInvoiceType;
        try {
            setSaving(true);
            // support onSave returning a promise
            await (onSave(payload) as unknown as Promise<void>);
        } finally {
            setSaving(false);
        }
    };

    // delete handled from table; modal has no delete button

    if (!edited) return null;

    return (
        <Modal show={!!invoice} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{edited.id ? "Редакция" : "Ново зареждане"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col>
                        <StringInputField label="Фактура №" value={edited.invNumber} onChange={!saving ? (value) => setField({invNumber: value}) : undefined}/>
                    </Col>
                    <Col>
                        <Form.Label>Дата</Form.Label>
                        <InputGroup className="mb-2 w-160px flex-nowrap">
                            <ReactDatePicker
                                id={"editInvDate"}
                                wrapperClassName={"w-100px"}
                                selected={edited.invDate}
                                onChange={(date: Date | null, _event?: any) => {
                                    if (!saving) setField({invDate: dateWithZeroTime(date || new Date())!});
                                }}
                                disabled={saving}
                                className="form-control flex-nowrap"
                                dateFormat="dd.MM.yyyy"
                                value={dateToString(edited.invDate)}
                            />
                            <label htmlFor={"editInvDate"}>
                                <InputGroup.Text className={"lh-2-5em"}>
                                    <FontAwesomeIcon icon={faCalendar} />
                                </InputGroup.Text>
                            </label>
                        </InputGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <SelectInputField label="Превозно средство" value={edited.truckId}
                                          options={( !edited.id ? [{ value: "", label: "" }] : [] ).concat((trucks || []).map((t: RRTruckType) => ({value: t.id || "", label: `${t.name}${t.plate ? ' (' + t.plate + ')' : ''}`})))}
                                          onChange={!saving ? (value) => setField({truckId: value || ""}) : undefined}
                        />
                    </Col>
                    <Col>
                        <SelectInputField label="Шофьор" value={edited.driverId}
                                          options={( !edited.id ? [{ value: "", label: "" }] : [] ).concat(filteredEmployees.map(e => ({value: (e.id || ""), label: e.name})))}
                                          onChange={!saving ? (value) => setField({driverId: value || ""}) : undefined}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <NumberInputField label="Цена за литър" value={edited.price} onChange={!saving ? (value) => setField({price: value || 0}) : undefined} disabled={saving}/>
                    </Col>
                    <Col>
                        <NumberInputField label="Количество гориво" value={edited.fuelAmount} onChange={!saving ? (value) => setField({fuelAmount: value || 0}) : undefined} disabled={saving}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <NumberInputField label="Начален км" value={edited.startKm} onChange={!saving ? (value) => setField({startKm: value || 0}) : undefined} disabled={saving}/>
                    </Col>
                    <Col>
                        <NumberInputField label="Краен км" value={edited.endKm} onChange={!saving ? (value) => setField({endKm: value || 0}) : undefined} disabled={saving}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <NumberInputField label="Общо за фактурата" value={computedTotalForInvoice} disabled formatter={(v) => zeroToEmptyStringAsCurrency(v || 0)} />
                     </Col>
                     <Col>
                        <NumberInputField label="Общо км" value={computedTotalKm} disabled formatter={(v) => numberWithCommas(Number(v || 0))} />
                     </Col>
                     <Col>
                        <NumberInputField label="Средна цена/км" value={computedAvgPriceForKm} disabled formatter={(v) => zeroToEmptyStringAsCurrency(v || 0)} />
                     </Col>
                 </Row>
                <StringInputField label="Бележки" value={edited.notes || ""} onChange={!saving ? (value) => setField({notes: value}) : undefined}/>
                {errors.length > 0 && (
                    <div className="text-danger mt-2">
                        {errors.map((e, idx) => <div key={idx}>{e}</div>)}
                    </div>
                )}
             </Modal.Body>
             <Modal.Footer>
                <div className="ms-auto">
                    {saving ? (
                        // while saving hide the Save/Cancel buttons and show a spinner only
                        <span className="ms-1"><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>
                    ) : (
                        <>
                            <SaveIconButton small onClick={doSave} disabled={errors.length > 0} />&nbsp;
                            <CancelIconButton small onClick={onClose} />
                        </>
                    )}
                </div>
             </Modal.Footer>
         </Modal>
     );
 }

 export default FuelInvoiceEditModal;
